import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import { getDrawerFormFix } from "utils/css";
import { validatePolicyName } from "utils/formValidators";
import { withTestId } from "utils/withTestId";

import { ADD_LOGIN_POLICY_DRAWER_TEST_ID } from "./constants";
import { CreatePolicyFields } from "./types";

type LoginPoliciesCreateDrawerProps = {
  takenPolicyNames: string[];
};

const LoginPoliciesCreateDrawer = createDrawer(
  ({ takenPolicyNames }: LoginPoliciesCreateDrawerProps) => {
    const navigate = useNavigate();

    const createPolicyForm = useForm<CreatePolicyFields>({
      defaultValues: {
        name: "",
        labels: [],
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = createPolicyForm;

    const drawer = useModal();

    const redirectToCreateView: SubmitHandler<CreatePolicyFields> = (formData) => {
      const labelsQuery =
        formData.labels && `&labels=${btoa(encodeURIComponent(JSON.stringify(formData.labels)))}`;

      drawer.hide();
      navigate(`/new/login-policy?name=${btoa(encodeURIComponent(formData.name))}${labelsQuery}`);
    };

    return (
      <DrawerForm isDirty={isDirty} {...withTestId(ADD_LOGIN_POLICY_DRAWER_TEST_ID)}>
        <FormProvider {...createPolicyForm}>
          <form onSubmit={handleSubmit(redirectToCreateView)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle title="Create policy" />
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody>
              <FormField label="Name" error={errors?.name?.message}>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Name of your new policy"
                    error={!!errors?.name}
                    {...register("name", {
                      required: "Name field is required.",
                      setValueAs: (value) => value.trim(),
                      validate: validatePolicyName(takenPolicyNames),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldTags name="labels" tagName="label" label="Labels" isOptional />

              <DrawerFooter>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button variant="primary" type="submit">
                    Continue
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showLoginPoliciesCreateDrawer = createDrawerTrigger(LoginPoliciesCreateDrawer);
